import {
	CurrentUserRoleLike,
	isOrganizationAdmin,
	isOrganizationAdminRole,
} from './hierarchySelection';

export interface SecurityRoleLike extends CurrentUserRoleLike {
	'@id'?: string;
}

export const canAssignOrganizationAdminRole = (
	currentUserRole: CurrentUserRoleLike | null
): boolean => isOrganizationAdmin(currentUserRole);

/**
 * Who may change the Organization Administrator on the org record.
 * Same privilege set as assigning the Organization Admin security role
 * (Organization Admin role, or admin@byod.ai).
 */
export const canChangeOrganizationAdministrator = (
	currentUserRole: CurrentUserRoleLike | null
): boolean => canAssignOrganizationAdminRole(currentUserRole);

/**
 * Returns security roles the logged-in user is allowed to assign.
 * Only Organization Admins and admin@byod.ai may assign the Organization Admin role.
 */
export const getAssignableSecurityRoles = <T extends SecurityRoleLike>(
	securityRoles: T[],
	currentUserRole: CurrentUserRoleLike | null
): T[] => {
	if (canAssignOrganizationAdminRole(currentUserRole)) {
		return securityRoles;
	}

	return securityRoles.filter((role) => !isOrganizationAdminRole(role));
};

export const isSecurityRoleAssignmentPermitted = (
	securityRoleResource: string | undefined,
	securityRoles: SecurityRoleLike[],
	currentUserRole: CurrentUserRoleLike | null
): boolean => {
	if (!securityRoleResource) return false;

	const selectedRole = securityRoles.find((role) => role['@id'] === securityRoleResource);
	if (!selectedRole) return false;

	return getAssignableSecurityRoles(securityRoles, currentUserRole).some(
		(role) => role['@id'] === securityRoleResource
	);
};

/**
 * Users who cannot assign/view Organization Admin on create also cannot edit
 * users who already have that role.
 */
export const canEditUserWithSecurityRole = (
	targetUserRole: CurrentUserRoleLike | null,
	currentUserRole: CurrentUserRoleLike | null
): boolean => {
	if (!isOrganizationAdminRole(targetUserRole)) return true;
	return canAssignOrganizationAdminRole(currentUserRole);
};

export { isOrganizationAdminRole };
